import Config, { defaultHeaders, HTTP } from '../config';
import { RemoteIdAuth } from '../config/types';
import {
  authenticatePasswordlessUserBySMSMutation,
  createPasswordlessUserByIdMutation,
  paymentMethodsQuery,
  paymentMethodQuery,
  makePaymentMutation,
  createPaymentMethodMutation
} from '../graphql/queries';

const headers = { ...defaultHeaders };

const { serviceUrl, appType, usersUrl, graphqlUrl, authenticatePasswordlessScope } = Config;

const jsonResponse = (body: unknown, status: number, statusText: string): Response =>
  new Response(JSON.stringify(body), { status, statusText });

const unprocessableEntity = (message: string): Response =>
  jsonResponse({ message }, HTTP.UNPROCESSABLE_ENTITY, 'Unprocessable Entity');

// Shared by every gateway GraphQL call below: a non-2xx response (e.g. 401/403 from edge auth, a
// 5xx, or a CDN/proxy error) can carry a non-JSON body, and `response.json()` would throw in that
// case — letting that reach fetchSaga's exception path clears identity/session, which is worse
// than just surfacing a failure. Returns the parsed body, or a Response already shaped for
// fetchSaga if parsing wasn't possible.
const parseGatewayResponse = async (
  response: Response,
  fallbackMessage: string,
): Promise<{ data: any; errors?: any[] } | Response> => {
  if (!response.ok) {
    // Preserve the real status/statusText (401/403/500/...) rather than collapsing every
    // failure to 422 — fetchSaga's status-based alert handling (and any caller-specific
    // customHandlers) needs the real code to show the right message.
    return jsonResponse({ message: fallbackMessage }, response.status, response.statusText);
  }
  try {
    return await response.json();
  } catch {
    return unprocessableEntity(fallbackMessage);
  }
};

export const authenticatePasswordlessUser = async (payload: Record<string, any>): Promise<any> =>
  fetch(graphqlUrl(), {
    method: 'POST',
    body: JSON.stringify({
      "query": authenticatePasswordlessUserBySMSMutation,
      "variables": {
        "input": {
          "code": payload.code,
          "mobile": payload.mobile,
          "scope": authenticatePasswordlessScope.join(" "),
          "audience": graphqlUrl()
        }
      }
    }),
    headers,
  })

export const createPasswordlessUserById = async (payload: RemoteIdAuth): Promise<any> =>
  fetch(`${usersUrl()}/graphql`, {
    method: 'POST',
    body: JSON.stringify({
      "query": createPasswordlessUserByIdMutation,
      "variables": {
          "input": {
              "userId": payload.remoteId,
          }
      }
    }),
    headers,
  });

export const refreshToken = async (refreshToken: string): Promise<any> => {
  return fetch(`${serviceUrl()}/identity/refresh`, {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
    headers,
  });
};

// Callers pass userId already formatted as finance-core's "U"-prefixed publicId (see
// getPublicUserId in utilities/index.ts) — these GraphQL calls send it through as-is.
export const listCpayMethods = async (payload: Record<string, any>): Promise<Response> => {
  const { accessToken, userId } = payload;
  const response = await fetch(graphqlUrl(), {
    method: 'POST',
    body: JSON.stringify({
      "query": paymentMethodsQuery,
      "variables": {
        "userId": userId,
      }
    }),
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
  });
  const result = await parseGatewayResponse(response, 'Failed to retrieve payment methods');
  if (result instanceof Response) {
    return result;
  }

  const { data, errors } = result;
  if (errors?.length) {
    return unprocessableEntity(errors[0].message || 'Failed to retrieve payment methods');
  }

  return jsonResponse(data?.paymentMethods || [], HTTP.OK, 'OK');
};


export const updatePaymentMethods = async (payload: Record<string, any>): Promise<any> => {
  const { accessToken, id, cardExpiry, userId } = payload;

  return fetch(`${serviceUrl()}/payment-methods/${id}?userId=${userId}`, {
    method: 'PUT',
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ cardExpiry }),
  });
};

export const getCreditCardUrl = async (): Promise<Response> =>
  fetch(`${serviceUrl()}/payment-gateway/iframe?source=${appType}`, {
    method: 'GET',
    headers,
  });

// finance-core's PaymentMethodSource/PaymentType GraphQL enums expose the TS enum's keys, not
// their underlying string values (same registerEnumType gotcha as PAYMENT_SOURCE_GRAPHQL_ENUM
// below) — map the legacy REST-shaped values BOOP already sends to their GraphQL literals here.
// PaymentMethodSource is a distinct enum from PaymentSource (used by submitApp below) even
// though their values happen to match today — kept separate so a future divergence can't
// silently break one via the other.
const PAYMENT_METHOD_SOURCE_GRAPHQL_ENUM: Record<string, string> = {
  BOOP: 'OneOffPayment',
};
const PAYMENT_METHOD_TYPE_GRAPHQL_ENUM: Record<string, string> = {
  debit: 'DEBIT',
  card: 'CARD',
};
// PaymentCardType's GraphQL literal is likewise the enum key, not the value the card-tokenization
// iframe actually sends (e.g. cardType: 'MasterCard') — same gotcha, same fix.
const PAYMENT_CARD_TYPE_GRAPHQL_ENUM: Record<string, string> = {
  VISA: 'VISA',
  MasterCard: 'MASTERCARD',
  AMEX: 'AMEX',
  JCB: 'JCB',
  Diners: 'DINERS',
  Discover: 'DISCOVER',
  'China UnionPay': 'CHINA_UNION_PAY',
  Unknown: 'UNKNOWN',
};

export const savePaymentMethod = async (payload: Record<string, any>): Promise<Response> => {
  const { accessToken, type, source, cardType, userId, ...rest } = payload;

  const response = await fetch(graphqlUrl(), {
    method: 'POST',
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({
      query: createPaymentMethodMutation,
      variables: {
        paymentMethod: {
          ...rest,
          // userId arrives already formatted as finance-core's "U"-prefixed publicId.
          userId,
          type: PAYMENT_METHOD_TYPE_GRAPHQL_ENUM[type] || type,
          source: PAYMENT_METHOD_SOURCE_GRAPHQL_ENUM[source] || source,
          // Debit methods have no cardType — omit the key entirely rather than sending an
          // explicit `undefined` (which JSON.stringify happens to drop today, but relying on
          // that instead of an explicit omission is fragile if a validator is ever added).
          ...(cardType ? { cardType: PAYMENT_CARD_TYPE_GRAPHQL_ENUM[cardType] || cardType } : {}),
        },
      },
    }),
  });
  const result = await parseGatewayResponse(response, 'Failed to save payment method');
  if (result instanceof Response) {
    return result;
  }

  const { data, errors } = result;
  if (errors?.length) {
    return unprocessableEntity(errors[0].message || 'Failed to save payment method');
  }

  // No errors, but also nothing to return — a downstream `methodId: null` sent to submitApp
  // would be a much harder-to-diagnose GraphQL validation failure than an explicit 422 here.
  if (!data?.createPaymentMethod) {
    return unprocessableEntity('Failed to save payment method');
  }

  return jsonResponse(data.createPaymentMethod, HTTP.CREATED, 'Created');
};

export const getCpayDetails = async (payload: Record<string, any>): Promise<Response> => {
  const { cpayMethodId, accessToken, userId } = payload;

  const response = await fetch(graphqlUrl(), {
    method: 'POST',
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({
      query: paymentMethodQuery,
      variables: {
        id: cpayMethodId,
        userId,
      },
    }),
  });
  const result = await parseGatewayResponse(response, 'Failed to retrieve payment method');
  if (result instanceof Response) {
    return result;
  }

  const { data, errors } = result;
  if (errors?.length) {
    // finance-core maps a missing payment method to a NOT_FOUND GraphQL error (see
    // findPaymentMethod's NotFoundException) rather than an HTTP 404 — reconstruct that status so
    // fetchSaga's existing HTTP.NOT_FOUND alert handling keeps working unchanged.
    const isNotFound = errors[0]?.extensions?.code === 'NOT_FOUND';
    return jsonResponse(
      { message: errors[0].message || 'Failed to retrieve payment method' },
      isNotFound ? HTTP.NOT_FOUND : HTTP.UNPROCESSABLE_ENTITY,
      isNotFound ? 'Not Found' : 'Unprocessable Entity',
    );
  }

  // No errors, but also no payment method — treat the same as finance-core's own NOT_FOUND
  // GraphQL error above rather than passing null through to the caller.
  if (!data?.paymentMethod) {
    return jsonResponse({ message: 'Payment method not found' }, HTTP.NOT_FOUND, 'Not Found');
  }

  return jsonResponse(data.paymentMethod, HTTP.OK, 'OK');
};

// finance-core's PaymentSource GraphQL enum exposes the TS enum's keys, not its underlying
// string values (registerEnumType default) — e.g. PaymentSource.OneOffPayment = 'BOOP', but the
// GraphQL literal is "OneOffPayment", not "BOOP". Map the legacy 'BOOP' source string (still sent
// as-is by submitApp's caller) to its GraphQL enum literal here.
const PAYMENT_SOURCE_GRAPHQL_ENUM: Record<string, string> = {
  BOOP: 'OneOffPayment',
};

export const submitApp = async (payload: Record<string, any>): Promise<Response> => {
  const { accessToken, source, userId, ...rest } = payload;

  const response = await fetch(graphqlUrl(), {
    method: 'POST',
    headers: { ...defaultHeaders, Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({
      query: makePaymentMutation,
      variables: {
        payment: {
          ...rest,
          source: PAYMENT_SOURCE_GRAPHQL_ENUM[source] || source,
          // userId arrives already formatted as finance-core's "U"-prefixed publicId.
          userId,
        },
      },
    }),
  });
  const result = await parseGatewayResponse(response, 'Failed to make payment');
  if (result instanceof Response) {
    return result;
  }

  const { data, errors } = result;
  if (errors?.length) {
    // finance-core's makePayment collapses every downstream failure (declined card, gateway
    // error, etc.) into one GraphQL error, with no status code to distinguish them anymore.
    // Map to the existing 422 alert rather than letting this bubble up as a fetch exception —
    // that path (fetchSaga's catch-all -> SET_RESPONSE_ERROR) clears identity/session, logging
    // the user out for what's normally just a declined payment.
    return unprocessableEntity(errors[0].message || 'Failed to make payment');
  }

  // finance-core's makePayment always returns { status, statusText } (see wrapCatch in
  // payments.resolver.ts), but treat it as untrusted input anyway — an out-of-range/non-integer
  // status or a statusText containing CRLF would throw constructing the Response (or allow
  // header injection), which would hit fetchSaga's exception path instead of the intended alert.
  // Fall back to a FAILING status (500, not 200) — a malformed status from finance-core is not
  // evidence the payment actually succeeded, and defaulting to OK would silently show success for
  // what might be a failed payment, the exact footgun this guard exists to prevent. Same reasoning
  // applies if `makePayment` itself is missing/null (no errors, but nothing to destructure either).
  if (!data?.makePayment) {
    return unprocessableEntity('Failed to make payment');
  }
  const { status, statusText } = data.makePayment;
  const safeStatus =
    Number.isInteger(status) && status >= 200 && status <= 599 ? status : HTTP.INTERNAL_SERVER_ERROR;
  const safeStatusText = typeof statusText === 'string' ? statusText.replace(/[\r\n]/g, '') : 'Internal Server Error';
  return jsonResponse(data.makePayment, safeStatus, safeStatusText);
};

// TODO test only
export const testSubmitApp = async (payload: Record<string, any>): Promise<any> =>
  new Promise<any>((resolve, reject) => {
    setTimeout(() => {
      resolve({ status: 400, message: 'some data' });
    }, 3000);
  });

export default {
  getCreditCardUrl,
  savePaymentMethod,
  getCpayDetails,
  submitApp,
  testSubmitApp,
};
